import type { ComponentType } from 'react';
import { StaticPageComponent } from '@tbbt/shared';
import { HomePage } from './HomePage';
import { BlogIndexPage, BlogPostPage } from './BlogPages';
import { ContactPage } from './ContactPage';
import {
  AboutPage,
  CareersPage,
  CustomPage,
  LegalPage,
  ServiceDetailPage,
  ServicesPage,
  TeamPage,
} from './StaticPages';

/**
 * Maps a STATIC page's `componentKey` to the React component that renders it.
 *
 * This registry is the join between the two halves of the page system: the database owns
 * which paths exist, their order, visibility and SEO; the code owns what each looks like.
 * Adding a page-backed screen is a two-step change — add the key in
 * `@tbbt/shared/constants`, register the component here — and `assertRegistryComplete`
 * below makes forgetting the second step a loud startup error instead of a blank screen.
 */
export const PAGE_COMPONENTS: Record<StaticPageComponent, ComponentType> = {
  [StaticPageComponent.HOME]: HomePage,
  [StaticPageComponent.SERVICES]: ServicesPage,
  [StaticPageComponent.SERVICE_DETAIL]: ServiceDetailPage,
  [StaticPageComponent.ABOUT]: AboutPage,
  [StaticPageComponent.TEAM]: TeamPage,
  [StaticPageComponent.BLOG_INDEX]: BlogIndexPage,
  [StaticPageComponent.BLOG_POST]: BlogPostPage,
  [StaticPageComponent.CONTACT]: ContactPage,
  [StaticPageComponent.CAREERS]: CareersPage,
  // The three legal pages share one template. They are separate keys rather than one so
  // each can be hidden, reordered and given its own SEO metadata independently.
  [StaticPageComponent.PRIVACY]: LegalPage,
  [StaticPageComponent.TERMS]: LegalPage,
  [StaticPageComponent.COOKIES]: LegalPage,
};

/** Fallback for CUSTOM pages, whose body comes from the CMS. */
export const CUSTOM_PAGE_COMPONENT = CustomPage;

/**
 * Verify every declared component key has an implementation.
 *
 * Called once when the router is built. The failure it prevents is specific and nasty: a
 * STATIC page row whose `componentKey` has no entry here renders nothing, so the page
 * loads, returns 200, and shows a blank area — which looks like a CSS bug and takes far
 * longer to diagnose than an explicit error naming the missing key.
 */
export function assertRegistryComplete(): void {
  const missing = Object.values(StaticPageComponent).filter((key) => !PAGE_COMPONENTS[key]);

  if (missing.length > 0) {
    throw new Error(
      `Page component registry is incomplete. No component registered for: ${missing.join(
        ', ',
      )}. Add them to apps/web/src/pages/public/registry.tsx.`,
    );
  }
}

/**
 * Resolve a component for a page from the route manifest.
 *
 * Returns `undefined` for an unrecognised key so the caller can decide — the router logs
 * a warning and falls back to the CMS template rather than crashing the whole app for one
 * bad row.
 */
export function resolvePageComponent(componentKey: string | null): ComponentType | undefined {
  if (componentKey === null) return CUSTOM_PAGE_COMPONENT;
  return PAGE_COMPONENTS[componentKey as StaticPageComponent];
}
